<script lang="ts">
    import type { Petition } from '../../api/petitions.ts';
    import * as t from '../../translations/Petitions.ts';
    import CaretRightIcon from '../../icons/CaretRightIcon.svelte';
    import AlarmClockIcon from '../../icons/AlarmClockIcon.svelte';
    import CheckIcon from '../../icons/CheckIcon.svelte';

    export let petition: Petition;
    export let hasSigned = false;
    export let onSign: (petition: Petition) => void = () => {};

    function formatDate(dateString?: string): string {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric' 
        });
    }

    function isEndingSoon(dateString?: string): boolean {
        if (!dateString) return false;
        const endDate = new Date(dateString);
        const now = new Date();
        const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return daysLeft > 0 && daysLeft <= 7;
    }

    function getDaysLeft(dateString?: string): number {
        if (!dateString) return 0;
        const endDate = new Date(dateString);
        const now = new Date();
        return Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    }

    function getDaysLeftLabel(daysLeft: number): string {
        return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
    }

    function getDescription(petition: Petition, maxLength: number = 250): string {
        // Prefer the pre-stripped plain-text alert; `description` now holds the raw
        // CKEditor HTML used by the sign page, which would render as markup here.
        const text = petition.petitionAlert?.shortenedAlert || petition.petitionAlert?.alert || '';
        if (text.length <= maxLength) return text;
        const truncated = text.substring(0, maxLength).replace(/\s+\S*$/, '');
        return truncated + '...';
    }

    function handleCardKeydown(event: KeyboardEvent): void {
        if (event.currentTarget !== event.target) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSign(petition);
        }
    }

    /** Avoid blank-page crashes if translation exports are missing at runtime (dev/stale bundle). */
    function safeTranslation(key: string, fallback: string): string {
        const candidate = (t as any)[key];
        if (typeof candidate === 'function') return candidate();
        if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate;
        return fallback;
    }

    function safeSignaturesLabel(count: number): string {
        const candidate = (t as any).Signatures_Label;
        if (typeof candidate === 'function') return candidate(count);
        return count === 1 ? `${count} signature` : `${count} signatures`;
    }
</script>

<div
    class="petition-card-shell group flex flex-col md:flex-row border border-gray-300 border-l border-l-gray-300 bg-white overflow-hidden h-auto rounded-lg transition-[transform,box-shadow,border-color,border-width] duration-300 ease-in-out transform-gpu origin-left will-change-transform hover:scale-[1.005] hover:shadow-lg hover:border-l-4 hover:border-l-[var(--vv-action-center-button-color,#0d9488)] active:border-l-4 active:border-l-[var(--vv-action-center-button-color,#0d9488)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vv-action-center-button-color,#0d9488)] focus-visible:ring-offset-2 active:outline-none cursor-pointer {petition.imageUrl ? 'md:h-[250px]' : 'md:h-[170px]'}"
    data-testid={`petition-${petition.id}`}
    data-signed={hasSigned ? 'true' : undefined}
    role="button"
    tabindex="0"
    aria-label={hasSigned ? `${safeTranslation('Signed_State_Label', 'Signed')}: ${petition.title}` : `Sign Petition: ${petition.title}`}
    on:click={() => onSign(petition)}
    on:keydown={handleCardKeydown}
>
    <!-- Left area - image or title/date (desktop only for no-image) with divider -->
    <div class="w-full md:w-2/5 flex-shrink-0 {petition.imageUrl ? '' : 'hidden md:block md:border-r md:border-gray-200'}">
        {#if petition.imageUrl}
            <img 
                src={petition.imageUrl} 
                alt={petition.title}
                class="w-full h-[160px] md:h-full object-cover"
                data-testid="image"
            />
        {:else}
            <!-- Desktop only: title/date on left side -->
            <div class="p-4 md:px-5 md:pb-5 md:pt-4 flex flex-col gap-1 h-full">
                <h2 class="petition-card-title !text-base md:!text-lg !font-semibold !text-gray-900 !leading-tight !m-0 line-clamp-2">
                    {petition.title}
                </h2>
                {#if petition.endDate}
                    <p class="petition-card-date !text-sm !leading-5 !text-gray-500 !m-0">
                        {safeTranslation('Due_By_Label', 'Due by')} {formatDate(petition.endDate)}
                    </p>
                {/if}
            </div>
        {/if}
    </div>
    
    <!-- Right area - content (full width on mobile for no-image cards) -->
    <div class="w-full md:w-3/5 p-4 md:p-5 flex flex-col justify-between" data-testid="content">
        <div class="flex flex-col gap-2">
            {#if petition.imageUrl}
                <!-- With image: show title and date here (all screens) -->
                <h2 class="petition-card-title !text-base md:!text-lg !font-semibold !text-gray-900 !leading-tight line-clamp-2">
                    {petition.title}
                </h2>
                {#if petition.endDate}
                    <p class="petition-card-date !text-sm !leading-5 !text-gray-500">
                        {safeTranslation('Due_By_Label', 'Due by')} {formatDate(petition.endDate)}
                    </p>
                {/if}
            {:else}
                <!-- No image: show title and date here on MOBILE only -->
                <div class="md:hidden">
                    <h2 class="petition-card-title !text-base !font-semibold !text-gray-900 !leading-tight line-clamp-2">
                        {petition.title}
                    </h2>
                    {#if petition.endDate}
                        <p class="petition-card-date !text-sm !leading-5 !text-gray-500 mt-1">
                            {safeTranslation('Due_By_Label', 'Due by')} {formatDate(petition.endDate)}
                        </p>
                    {/if}
                </div>
            {/if}
            
            <!-- Description -->
            <p class="petition-card-description !text-sm !leading-6 !text-gray-600 line-clamp-2">
                {getDescription(petition)}
            </p>
        </div>
        
        <!-- CTA Section - Full width on mobile, inline on desktop -->
        <div class="flex flex-col md:flex-row md:items-center gap-3 pt-3">
            <!-- Visual CTA; card itself handles click and keyboard activation -->
            <div
                class="petition-card-sign-button w-full md:w-auto md:min-w-[140px] md:max-w-[160px] min-h-[48px] md:min-h-[44px] !text-base !leading-6 !transition-all rounded !px-2 !py-1 text-center border border-solid inline-flex items-center {hasSigned ? 'petition-card-sign-button--signed !bg-emerald-50/80' : '!bg-white group-hover:!bg-[#e5e5e5] group-active:!bg-[#d4d4d4] border-gray-200'}"
                aria-hidden="true"
            >
                <div class="flex w-full justify-center md:justify-between items-center gap-1.5">
                    {#if hasSigned}
                        <span class="inline-flex items-center gap-1.5 text-emerald-700 font-medium">
                            <CheckIcon width="18" height="18" class="flex-shrink-0 text-emerald-600" aria-hidden="true" />
                            <span>{safeTranslation('Signed_State_Label', 'Signed')}</span>
                        </span>
                    {:else}
                        <span>{safeTranslation('Sign_Petition_Button', 'Sign Petition')}</span>
                    {/if}
                    <span
                        class="inline-flex items-center flex-shrink-0 {hasSigned
                            ? 'text-emerald-600'
                            : 'text-[var(--vv-action-center-button-color,#0d9488)]'}"
                    >
                        <CaretRightIcon aria-hidden="true" />
                    </span>
                </div>
            </div>
            
            <!-- Supporting info: badges and signature count -->
            <div class="flex flex-wrap items-center justify-center md:justify-between gap-3 md:gap-4 w-full md:w-auto md:flex-1">
                <!-- Urgency badge - days left (left side) -->
                <div class="flex items-center gap-3">
                    {#if isEndingSoon(petition.endDate)}
                        <span class="petition-card-ending-badge inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium whitespace-nowrap" role="status" aria-label="{getDaysLeftLabel(getDaysLeft(petition.endDate))} to sign">
                            <AlarmClockIcon width="12" height="12" aria-hidden="true" />
                            <span>{getDaysLeftLabel(getDaysLeft(petition.endDate))}</span>
                        </span>
                    {/if}
                </div>
                <!-- Signatures count (right side on desktop, centered on mobile) -->
                {#if petition.signatureCount !== undefined && petition.signatureCount > 0}
                    <div class="petition-card-signature-count inline-flex items-center !text-sm !leading-5 !text-gray-500" aria-label="{safeSignaturesLabel(petition.signatureCount)}">
                        <span>{safeSignaturesLabel(petition.signatureCount)}</span>
                    </div>
                {/if}
            </div>
        </div>
    </div>
</div>

<style>
    :global(.petition-card-shell) {
        font-size: 1rem !important;
        line-height: 1.5rem !important;
    }

    :global(.petition-card-title) {
        font-size: 1.125rem !important;
        line-height: 1.5rem !important;
        font-weight: 600 !important;
        color: #111827 !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }

    :global(.petition-card-date) {
        font-size: 0.875rem !important;
        line-height: 1.25rem !important;
        font-weight: 400 !important;
        color: #6b7280 !important;
    }

    :global(.petition-card-description) {
        font-size: 0.875rem !important;
        line-height: 1.5rem !important;
        font-weight: 400 !important;
        color: #4b5563 !important;
    }

    :global(.petition-card-sign-button) {
        border: 1px solid #d1d5db !important;
        background-color: #ffffff !important;
        color: #111827 !important;
        box-sizing: border-box !important;
        padding: 0.5rem 0.875rem !important;
        font-size: 1rem !important;
        line-height: 1.5rem !important;
        font-weight: 500 !important;
    }

    :global(.petition-card-sign-button:hover) {
        border-color: #d1d5db !important;
    }

    :global(.petition-card-sign-button--signed) {
        border-color: #6ee7b7 !important;
        background-color: #ecfdf5 !important;
    }

    :global(.petition-card-sign-button--signed:hover) {
        border-color: #34d399 !important;
        background-color: #d1fae5 !important;
    }

    :global(.petition-card-ending-badge) {
        font-size: 0.75rem !important;
        line-height: 1rem !important;
        font-weight: 500 !important;
    }

    :global(.petition-card-signature-count) {
        font-size: 0.875rem !important;
        line-height: 1.25rem !important;
        font-weight: 400 !important;
        color: #6b7280 !important;
    }
</style>
